import { elevationText } from "./elevation-text";
import { awayWord, nearWord, spotWord } from "./place-word";
import type { RouteResult } from "@flowsight/sim";
import { buildShareUrl, buildLineText, fmtDays, shareToLine } from "./share";
import { routeTo, onSameRiver, nearestMainRiver } from "./sim-client";
import { loadRiverNames, displayRiverName as riverName } from "./river-names";
import { ENABLE_CAMERAS } from "./config";
import { loadGaugeData, nearestFloodGauge, floodGauges, depthIcon, depthLabel } from "./bkk-layer";
import { nearestThaiWater, ensureThaiWaterData, thaiWaterLoaded } from "./thaiwater-layer";
import { loadRain, rainCard, adviceEl, adviceReason, showRainChips, hideRainChips, setRainDayHandler } from "./rain-forecast";
import { decideAdvice } from "@flowsight/sim";
import { telize } from "./depth-ui";
import { emergencyCard } from "./emergency";
import { loadDamRain, loadDamRainOrThrow, damRainCard, type DamRainData } from "./dam-rain";
import { loadLowerRiver, lowerRiverCard } from "./lower-river";
import { loadAirQuality, airQualityCard } from "./air-quality";
import { resetSummary, publish, okDim, errDim, naDim } from "./summary-store";
import { setUserPinElev } from "./user-pin";
import { loadTwStation } from "./tw-trend";
import { stationsInRadius } from "./thaiwater-layer";
import { SUMMARY_RULES } from "@flowsight/sim";
import type { StationInfo } from "./summary-store";
type StationInfoOutside = NonNullable<StationInfo["outside"]>;
import type { FlowStatus } from "./route-layer";
import { SRC_FULL, thaiDay, damReleaseChange, c13Change, c13Streak, releaseBarText, type DamPrev, type C13Prev, type C13Recent } from "./dam-change-ui";
import { cachedFetch } from "./net-cache";

/** Plain-language summary card for the one-button "see water near me" flow. */

interface FloodSeries { time: string[]; q: number[]; min: number[]; max: number[]; }
interface FlowReading { status: FlowStatus; statusText: string; trendText: string; currentCms: number | null; }

export interface SummaryCallbacks {
  radiusKm: number;
  onClear: () => void;
  onRadius: (km: number) => void;
  onRadiusCommit: (km: number) => void;
  onFlowStatus: (s: FlowStatus) => void;
  onPlace: (place: Place | null) => void;
  onCameras: () => void;
  onDamZones: (data: DamRainData | null, dayIndex: number) => void; // Part B map colouring (B3)
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, attrs: Record<string, string> = {}, ...kids: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) { if (k === "html") n.innerHTML = v; else n.setAttribute(k, v); }
  n.append(...kids);
  return n;
}

async function copy(text: string, btn: HTMLElement): Promise<void> {
  try { await navigator.clipboard.writeText(text); }
  catch { const ta = document.createElement("textarea"); ta.value = text; document.body.append(ta); ta.select(); document.execCommand("copy"); ta.remove(); }
  const prev = btn.textContent; btn.textContent = "คัดลอกแล้ว ✓"; setTimeout(() => { btn.textContent = prev; }, 1600);
}

export function renderSummary(
  host: HTMLElement, r: RouteResult, from: { lat: number; lon: number }, cb: SummaryCallbacks,
  opts?: { centerLabel?: string | undefined; back?: (() => void) | undefined },
): void {
  const myGen = ++renderGen;
  resetSummary(myGen, from, cb.radiusKm, opts?.centerLabel ?? null); // v0.9.5 สรุป: every dimension → loading
  // v0.9.5a D1: the river the summary follows = this route's snapped reach (≤30 km, not the radius)
  // v0.9.9 C1: "แม่น้ำใกล้คุณ" = the nearest MAIN river (order ≥ 5) ≤30 km — same for the summary row,
  // the 〰 chip and the 📍 label; a closer smaller stream (where the route starts) rides along as "nearest"
  const pubRiver = (): void => {
    const main = nearestMainRiver(from.lon, from.lat, SUMMARY_RULES.riverMaxKm);
    if (!main) { publish(myGen, "river", naDim); return; }
    const own = r.snapped.id !== main.id && r.snapped.distKm < main.distKm ? riverName(r.snapped.id) : null;
    publish(myGen, "river", okDim({ name: riverName(main.id), distKm: main.distKm, reachId: main.id,
      nearest: own && own !== riverName(main.id) ? { name: own, distKm: r.snapped.distKm } : null }));
  };
  pubRiver();
  void loadElevation(from, myGen); // v0.9.10 B: once per chosen point (tap / search / 💧 / shared link)
  if (!riverName(r.snapped.id)) void loadRiverNames().then(() => { if (myGen === renderGen) pubRiver(); });
  hideRainChips(); // clear any previous point's day-chips bar
  const eta = `${fmtDays(r.etaDaysFast)}–${fmtDays(r.etaDaysSlow)}`;
  let place: Place | null = null;
  // a search-area center shows a fixed "reference point" label (not a house); a
  // gps/tap point reverse-geocodes to "ต./อ./จ.".
  const placeLine = el("div", { class: "place-line", id: "place-line" }, opts?.centerLabel ?? "📍 กำลังหาชื่อสถานที่…");
  // ONE prominent sentence by priority (gauges → satellite → declared → calm);
  // set by loadFlood once status + gauge count are known.
  const headLine = el("div", { class: "head-line loading", id: "head-line" }, "กำลังตรวจสถานการณ์น้ำ…");
  const headline = r.reachesGulf
    ? el("div", { class: "res-head gulf" }, "💧 น้ำบริเวณนี้ ", el("b", {}, "ไหลลงอ่าวไทย"))
    : el("div", { class: "res-head other" }, "💧 น้ำบริเวณนี้ ", el("b", {}, "ไม่ไหลลงอ่าวไทย"), ` (ไหลลง${r.destinationName})`);

  // live status chips (filled after /api/flood)
  const nowChip = el("b", { id: "now-chip", class: "chip loading" }, "กำลังโหลด…");
  const trendChip = el("b", { id: "trend-chip", class: "chip loading" }, "—");
  const riverOutside = r.snapped.distKm > cb.radiusKm; // A1
  // river/canal name (v0.6.4) — look up the snapped reach; fill async
  const nm = riverName(r.snapped.id);
  const riverFactValue = `${nm ?? "ลำน้ำไม่ทราบชื่อ"} · ${r.snapped.distKm.toFixed(1)} กม.`;
  const riverFact = riverOutside
    ? fact("ลำน้ำหลัก", `ไม่มีในรัศมี ${cb.radiusKm} กม.`)
    : fact(`ลำน้ำ${nearWord()}`, riverFactValue);
  if (!riverOutside) {
    riverFact.id = "river-fact";
    if (!nm) {
      void loadRiverNames().then(() => {
        if (myGen !== renderGen) return;
        const n2 = riverName(r.snapped.id);
        const vEl = document.querySelector("#river-fact b");
        if (n2 && vEl) vEl.textContent = `${n2} · ${r.snapped.distKm.toFixed(1)} กม.`;
      });
    }
  }
  const facts = el(
    "div", { class: "facts" },
    riverFact,
    el("div", { class: "fact" }, el("span", {}, "น้ำตอนนี้"), nowChip),
    // แนวโน้ม 3 วัน: only when a river is within the radius
    ...(riverOutside ? [] : [el("div", { class: "fact", id: "trend-fact" }, el("span", {}, "แนวโน้ม 3 วัน"), trendChip)]),
  );
  const floodNote = el("div", { class: "flood-note", id: "flood-note" }, "กำลังตรวจพื้นที่น้ำท่วม (GISTDA)…");
  const depthHost = el("div", { class: "depth-host", id: "depth-host" });
  const gaugeHost = el("div", { class: "gauge-host", id: "gauge-host" });
  const damBanner = el("div", { class: "dam-banner-host", id: "dam-banner" });
  const damChangeHost = el("div", { class: "dam-change-host", id: "dam-change" }); // ระบายเพิ่ม/ลด vs previous report (v0.9.4a A3ค)
  const lowerRiverHost = el("div", { class: "lower-river-host", id: "lower-river" }); // แม่น้ำตอนล่าง / น้ำทะเลหนุน (v0.9.4c)
  const rainHost = el("div", { class: "rain-host", id: "rain-host" }); // 7-day rain (v0.9.3 A), fill async
  const airHost = el("div", { class: "air-host", id: "air-host" }); // คุณภาพอากาศ (CAMS/Open-Meteo, v0.9.11), fill async
  const damRainHost = el("div", { class: "dam-rain-host", id: "dam-rain-host" }); // เหนือ/ท้ายเขื่อน (v0.9.3 B), fill async

  // ETA to the gulf — moved under "ขั้นสูง" (0c); fastest-bound shown big (range small)
  const etaAdvanced = el("details", { class: "eta-advanced" },
    el("summary", {}, "⚙️ ขั้นสูง: เส้นทางน้ำ & เวลาถึงปลายทาง"),
    el("div", { class: "res-eta" },
      el("div", { class: "eta-lead" }, "อาจถึงเร็วสุด ~", el("b", {}, fmtDays(r.etaDaysFast)), " วัน"),
      el("div", { class: "eta-range-sm" }, `ช่วงประมาณ ${eta} วัน · ${r.reachesGulf ? "ถึงปากอ่าวไทย" : "ถึง" + r.destinationName}`),
      el("div", { class: "eta-note" }, "แบบจำลอง · ยังไม่เทียบสถานีจริง"),
    ),
  );

  // radius control (0.5–50 km) + number box
  const slider = el("input", { type: "range", min: "0.5", max: "50", step: "0.5", value: String(cb.radiusKm), class: "range", id: "r-slider" }) as HTMLInputElement;
  const numBox = el("input", { type: "number", min: "0.5", max: "50", step: "0.5", value: String(cb.radiusKm), class: "numbox", id: "r-num" }) as HTMLInputElement;
  const liveCount = el("span", { class: "r-live", id: "r-live" }, "");
  // input = live (mask reveal + counter, no API); change/commit = recompute (debounced)
  const applyLive = (v: number): void => { const km = Math.min(50, Math.max(0.5, v || 3)); slider.value = String(km); numBox.value = String(km); unit.textContent = String(km); cb.onRadius(km); };
  const applyCommit = (v: number): void => { const km = Math.min(50, Math.max(0.5, v || 3)); cb.onRadiusCommit(km); };
  slider.addEventListener("input", () => applyLive(Number(slider.value)));
  slider.addEventListener("change", () => applyCommit(Number(slider.value)));
  numBox.addEventListener("input", () => applyLive(Number(numBox.value)));
  numBox.addEventListener("change", () => applyCommit(Number(numBox.value)));
  const radius = el("div", { class: "radius" },
    el("div", { class: "radius-head" }, el("span", {}, "รัศมีรอบตัว"), el("b", {}, el("span", { id: "r-unit" }), " กม."), liveCount),
    el("div", { class: "radius-row" }, slider, numBox),
  );
  const unit = radius.querySelector("#r-unit")!;
  unit.textContent = String(cb.radiusKm);

  const chart = el("div", { class: "res-chart", id: "res-chart" },
    el("div", { class: "chart-title" }, "แนวโน้มน้ำท่า (GloFAS)"), el("div", { class: "muted small" }, "กำลังโหลด…"));

  const badge = emergencyCard(); // emergency numbers (replaces the old disclaimer, v0.9.3)

  const lineBtn = el("button", { class: "btn primary" }, "📤 แชร์ทาง LINE");
  lineBtn.addEventListener("click", () => void shareToLine(buildLineText(r, from, opts?.centerLabel ?? place?.label ?? null, cb.radiusKm)).then((how) => {
    if (how === "copied") { const prev = lineBtn.textContent; lineBtn.textContent = "คัดลอกแล้ว ✓ วางใน LINE ได้เลย"; setTimeout(() => { lineBtn.textContent = prev; }, 1800); }
  }));
  const shareBtn = el("button", { class: "btn" }, "คัดลอกลิงก์");
  shareBtn.addEventListener("click", () => void copy(buildShareUrl(from, cb.radiusKm), shareBtn));
  const clearBtn = el("button", { class: "btn ghost" }, "ปิด");
  clearBtn.addEventListener("click", cb.onClear);
  const actionKids: Node[] = [lineBtn, shareBtn, clearBtn];
  if (opts?.back) {
    const backBtn = el("button", { class: "btn ghost" }, "← จุดก่อนหน้า");
    backBtn.addEventListener("click", opts.back);
    actionKids.unshift(backBtn);
  }
  const actions = el("div", { class: "res-actions" }, ...actionKids);

  // cameras are hidden entirely until permission (ENABLE_CAMERAS, v0.8.2)
  const camsKids: Node[] = [];
  if (ENABLE_CAMERAS) {
    const camsBtn = el("button", { class: "btn wide" }, "📹 กล้องจราจรในวง");
    camsBtn.addEventListener("click", () => cb.onCameras());
    camsKids.push(el("div", { class: "adv-cams" }, camsBtn));
  }

  // heavier detail collapses under one disclosure — the header stays the hero
  const moreDetails = el("details", { class: "more" },
    el("summary", {}, `ดูรายละเอียด (ลำน้ำ · แนวโน้ม · เขื่อน · เส้นทางน้ำ)`),
    headline, facts, floodNote, damBanner, depthHost, chart, etaAdvanced, ...camsKids,
  );

  host.replaceChildren(el("div", { class: "result card-in" }, placeLine, headLine, gaugeHost, damChangeHost, lowerRiverHost, radius, moreDetails, damRainHost, rainHost, airHost, badge, actions));

  void loadDamBanner(r, from, myGen);
  void loadDamChange(r, from, myGen, damChangeHost);
  void loadLowerRiverCard(from, myGen, lowerRiverHost);
  void loadGauge(from, cb.radiusKm, myGen);
  void loadRainCard(from, myGen, rainHost, cb.radiusKm);
  void loadAirQualityCard(from, myGen, airHost);
  void loadDamRainCard(from, myGen, damRainHost, cb.onDamZones);

  void loadFlood(from, cb.onFlowStatus, {
    radiusKm: cb.radiusKm, snapKm: r.snapped.distKm, gen: myGen, centerLabel: opts?.centerLabel, reachId: r.snapped.id, path: r.path,
    onPlace: (p) => { if (myGen !== renderGen) return; place = p; if (!opts?.centerLabel && !p) { const pl = document.getElementById("place-line"); if (pl) pl.textContent = ""; } cb.onPlace(p); },
  });
}

/** 7-day rain forecast at the user's point (v0.9.3 A). From /api/rain (ensemble);
 *  no data → nothing shown. Also shows the day-chips bar when a heavy+ day is near. */
async function loadRainCard(from: { lat: number; lon: number }, myGen: number, host: HTMLElement, radiusKm: number): Promise<void> {
  const data = await loadRain(from.lat, from.lon);
  if (myGen !== renderGen) return;
  if (!data) { publish(myGen, "rain", errDim); publish(myGen, "advice", errDim); return; }
  publish(myGen, "rain", okDim(data));
  // Part D: gather the "เตรียมตัว" advice signals from real data only
  let advice: HTMLElement | null = null;
  try {
    const [dam, gistda] = await Promise.all([loadDamRain(from.lat, from.lon), fetchGistdaStatus(from)]);
    await ensureThaiWaterData().catch(() => undefined);
    if (myGen !== renderGen) return;
    const tw = nearestThaiWater(from.lat, from.lon, radiusKm);
    const dec = decideAdvice({
      pointDays: data.days.map((d) => ({ m: d.m, veryHeavyCount: d.veryHeavyCount })),
      damDownDays: dam?.down?.days.map((d) => ({ m: d.m, veryHeavyCount: d.veryHeavyCount })) ?? null,
      isBelowDam: dam?.relation === "below",
      gistdaFloodInRadius: gistda.level === "flooded" || gistda.level === "near",
      thaiWaterOverBankInRadius: tw?.status === "over",
      ddpmAnnounced: gistda.declared,
    });
    publish(myGen, "advice", okDim({ show: dec.show, reason: dec.show ? adviceReason(dec, data, dam?.dam ?? null) : "" }));
    if (dec.show) { advice = adviceEl(dec, data, dam?.dam ?? null); void fetch("/api/advice-ping").catch(() => undefined); } // D7 counter
  } catch { publish(myGen, "advice", errDim); /* advice is best-effort; the rain card still renders */ }
  if (myGen !== renderGen) return;
  const card = rainCard(data, advice);
  host.replaceChildren(card);
  showRainChips(data);
  setRainDayHandler((i) => {
    const btn = card.querySelectorAll<HTMLElement>(".rf-day")[i];
    btn?.click();
    card.scrollIntoView({ behavior: "smooth", block: "nearest" });
  });
}

/** คุณภาพอากาศ at the user's point (v0.9.11). From /api/air-quality (CAMS via Open-Meteo);
 *  no data → the host stays empty (hidden by CSS). Information only — never an input of the flood summary. */
async function loadAirQualityCard(from: { lat: number; lon: number }, myGen: number, host: HTMLElement): Promise<void> {
  const data = await loadAirQuality(from.lat, from.lon);
  if (myGen !== renderGen) return;
  if (!data) return;
  const card = airQualityCard(data);
  if (card) host.replaceChildren(card);
}

/** Minimal GISTDA flood level + ปภ.-declared flag for the advice decision (D1(b)/D4). */
async function fetchGistdaStatus(from: { lat: number; lon: number }): Promise<{ level?: string; declared: boolean }> {
  try {
    const r = await cachedFetch(`/api/gistda/flood?lat=${from.lat}&lon=${from.lon}`);
    if (!r.ok) return { declared: false };
    const g = (await r.json()) as { level?: string; place?: { declared?: boolean } };
    const out: { level?: string; declared: boolean } = { declared: g.place?.declared === true };
    if (g.level != null) out.level = g.level;
    return out;
  } catch { return { declared: false }; }
}

/** เหนือเขื่อน/ท้ายเขื่อน rain at the user's point (v0.9.3 Part B). From /api/dam-rain;
 *  no related dam → nothing shown. Tapping a day colours the two zones on the map. */
async function loadDamRainCard(from: { lat: number; lon: number }, myGen: number, host: HTMLElement, onDamZones: (d: DamRainData | null, i: number) => void): Promise<void> {
  let data: DamRainData | null;
  try { data = await loadDamRainOrThrow(from.lat, from.lon); }
  catch { if (myGen === renderGen) publish(myGen, "damRain", errDim); return; }
  if (myGen !== renderGen) return;
  // summary: no related dam → row hidden; related but rain failed → "ไม่มีข้อมูลตอนนี้"
  publish(myGen, "damRain", !data ? naDim : data.up || data.down ? okDim(data) : errDim);
  if (!data || (!data.up && !data.down)) return;
  host.replaceChildren(damRainCard(data, (i) => onDamZones(data, i)));
}

export function renderMessage(host: HTMLElement, html: string): void {
  renderGen++; // invalidate any pending loaders from a previous result
  hideRainChips();
  host.replaceChildren(el("div", { class: "result card-in" }, el("div", { class: "res-msg" }, el("span", { html }))));
}

export function clearResult(host: HTMLElement): void { renderGen++; hideRainChips(); host.replaceChildren(); }

function fact(label: string, value: string): HTMLElement {
  return el("div", { class: "fact" }, el("span", {}, label), el("b", {}, value));
}

// Render generation: every new result/message bumps this. Async loaders capture
// the generation and bail out before touching the DOM if a newer render happened
// (fixes the "card stuck on the old point" bug when GPS/tap changes).
let renderGen = 0;

export interface Place { label: string; t: string; a: string; p: string; bkk: boolean; declared: boolean; declaredDate: string | null; }
interface GistdaStatus { level?: "flooded" | "near" | "none"; window?: "3days" | "7days" | null; observedAt?: string | null; nearestKm?: number | null; source?: string; error?: boolean; place?: Place | null; }
interface FloodCtx { radiusKm: number; snapKm: number; gen: number; centerLabel?: string | undefined; onPlace: (place: Place | null) => void; reachId: number; path: number[]; }

const WINDOW_TH: Record<string, string> = { "3days": "ภายใน 3 วัน", "7days": "รอบ 7 วัน" };

/** Loads live conditions. Priority (safety — never a false "ปกติ"):
 *  GISTDA flooded/near → ปภ.-declared province → GISTDA-uncheckable →
 *  (no river in radius) → GloFAS flow status (+ community caveat). */
async function loadFlood(from: { lat: number; lon: number }, onStatus: (s: FlowStatus) => void, ctx: FloodCtx): Promise<void> {
  const chart = document.getElementById("res-chart");
  const nowChip = document.getElementById("now-chip");
  const trendChip = document.getElementById("trend-chip");
  const floodNote = document.getElementById("flood-note");

  // GISTDA (flood area) + GloFAS (flow trend) are independent → start both at once and
  // process in order, so the point's status no longer waits for the two round-trips in series.
  const gistdaReq = cachedFetch(`/api/gistda/flood?lat=${from.lat}&lon=${from.lon}`).catch(() => null);
  const floodReq = cachedFetch(`/api/flood?lat=${from.lat}&lon=${from.lon}`).catch(() => null);

  // 1) GISTDA flood-area check for the clicked point
  let g: GistdaStatus;
  try {
    const gr = await gistdaReq;
    if (!gr) throw new Error("gistda");
    g = gr.ok ? ((await gr.json()) as GistdaStatus) : (await gr.json().then((j) => ({ error: true, province: j.province })).catch(() => ({ error: true })));
  } catch { g = { error: true }; }
  publish(ctx.gen, "gistda", g.error ? errDim : okDim({
    level: g.level ?? null, nearestKm: g.nearestKm ?? null, observedAt: g.observedAt ?? null, source: g.source ?? null,
    floodedInRadius: g.level === "flooded" || (g.level === "near" && g.nearestKm != null && g.nearestKm <= ctx.radiusKm),
  }));
  publish(ctx.gen, "place", g.place ? okDim(g.place) : errDim);

  // 2) GloFAS: trend + Q(t) chart (independent of flood status)
  let reading: FlowReading | null = null;
  try {
    const res = await floodReq;
    if (!res || !res.ok) throw new Error(res ? String(res.status) : "flood");
    const d = (await res.json() as { daily?: Record<string, (number | null)[]> & { time?: string[] } }).daily;
    if (!d?.time || !d["river_discharge"]) throw new Error("no series");
    const series: FloodSeries = {
      time: d["time"], q: num(d["river_discharge"]),
      min: num(d["river_discharge_min"] ?? d["river_discharge_p25"] ?? d["river_discharge"]),
      max: num(d["river_discharge_max"] ?? d["river_discharge_p75"] ?? d["river_discharge"]),
    };
    reading = readFlow(series);
    if (trendChip) { trendChip.className = "chip"; trendChip.textContent = reading.trendText; }
    if (chart) chart.replaceChildren(
      el("div", { class: "chart-title" }, "แนวโน้มน้ำท่า 3 สัปดาห์ (GloFAS, มี ensemble)"),
      sparkline(series, todayIndex(series.time)),
      el("div", { class: "chart-foot" }, reading.currentCms != null ? `ตอนนี้ ~${Math.round(reading.currentCms)} ลบ.ม./วิ` : "ไม่มีข้อมูลน้ำท่า"),
    );
  } catch {
    if (trendChip) { trendChip.className = "chip"; trendChip.textContent = "—"; }
    if (chart) chart.replaceChildren(el("div", { class: "chart-title" }, "แนวโน้มน้ำท่า"), el("div", { class: "muted small" }, "ข้อมูลน้ำท่าไม่พร้อม"));
  }

  // stale-card guard: a newer result rendered while awaiting → stop (don't write
  // this (old) point's place/flood into the current card).
  if (ctx.gen !== renderGen) return;

  // place name (ต./อ./จ.) — update card header + pin + LINE state
  const place = g.place ?? null;
  ctx.onPlace(place);
  const placeLine = document.getElementById("place-line");
  if (placeLine && place && !ctx.centerLabel) placeLine.textContent = `📍 ${place.label}`;

  // bail if a newer result rendered while we awaited (stale-card guard)
  if (ctx.gen !== renderGen) return;

  // 3) "น้ำตอนนี้" — priority, never a false "ปกติ"
  if (!nowChip) return;
  const setNote = (text: string, stale = false): void => { if (floodNote) { floodNote.className = `flood-note${stale ? " stale" : ""}`; floodNote.textContent = text; } };
  // never show a "ลดลง" trend in a flood/declared area — hide the trend row
  const hideTrend = (): void => { const t = document.getElementById("trend-fact"); if (t) t.style.display = "none"; };
  const prov = place;
  // C1: always the announcement date ("ประกาศ ปภ. ณ 30 ก.ย. 69"); older than 72 h → not re-checked since + link
  const ddBE = prov?.declaredDate ? (() => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(prov.declaredDate!); return m ? `${Number(m[3])} ${["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."][Number(m[2]) - 1]} ${String(Number(m[1]) + 543).slice(-2)}` : prov.declaredDate; })() : "";
  const ddStale = !!prov?.declaredDate && Date.now() - Date.parse(`${prov.declaredDate}T00:00:00+07:00`) > SUMMARY_RULES.ddpmStaleHours * 3600e3;
  const declaredNote = prov?.declared ? `${prov.bkk ? "เขต" + prov.a : "อ." + prov.a + " จ." + prov.p}มีประกาศเขตน้ำท่วม (ประกาศ ปภ.${ddBE ? " ณ " + ddBE : ""})${ddStale ? " · ยังไม่ได้ตรวจการยกเลิกประกาศหลังวันนี้" : ""}` : "";
  const ddpmLink = (): void => {
    if (!ddStale || !floodNote || floodNote.querySelector(".ddpm-link")) return;
    floodNote.append(" · ", el("a", { class: "ddpm-link", href: "https://www.disaster.go.th/", target: "_blank", rel: "noopener" }, "ดูเว็บ ปภ. ↗"));
  };

  // ── ONE prominent header sentence by priority (v0.8.0 item 4) ──
  let gaugesOk = true;
  await loadGaugeData().catch(() => { gaugesOk = false; });
  await ensureThaiWaterData().catch(() => undefined);
  if (ctx.gen !== renderGen) return;
  const gaugeCount = floodGauges(from, ctx.radiusKm).fresh.length;
  // v0.9.5 สรุป: road gauges (Bangkok network only) + ThaiWater stations in the radius
  {
    const deepest = floodGauges(from, ctx.radiusKm).fresh[0] ?? null; // sorted deepest first
    publish(ctx.gen, "roadGauge", !gaugesOk ? (place?.bkk ? errDim : naDim)
      : deepest ? okDim({ maxDepthCm: deepest.overtopCm, name: deepest.name, measuredAt: deepest.measuredAt, distKm: deepest.distKm })
      : place?.bkk ? okDim({ maxDepthCm: null, name: null, measuredAt: null, distKm: null }) : naDim);
    const list = stationsInRadius(from.lat, from.lon, ctx.radiusKm);
    const over = list.filter((s) => s.status === "over" && s.diffBankM != null).sort((a, b) => b.diffBankM! - a.diffBankM!)[0] ?? null;
    const near = list.find((s) => s.status === "near" || (s.status === "over" && s.diffBankM != null && s.diffBankM < SUMMARY_RULES.overBankM)) ?? null;
    const n0 = list[0] ?? null;
    // D2: nothing inside the radius → nearest fresh station on the SAME river within 30 km
    let outside: StationInfoOutside | null = null;
    if (!list.length) {
      const pathSet = new Set(ctx.path);
      const cand = stationsInRadius(from.lat, from.lon, SUMMARY_RULES.riverMaxKm).find((s) => onSameRiver(s.lon, s.lat, ctx.reachId, pathSet)) ?? null;
      if (cand) outside = { sid: cand.sid ?? null, name: cand.name, river: cand.river, status: cand.status, diffBankM: cand.diffBankM, distKm: cand.distKm, datetime: cand.datetime };
    }
    publish(ctx.gen, "station", thaiWaterLoaded() ? okDim({
      count: list.length,
      nearest: n0 ? { sid: n0.sid ?? null, name: n0.name, river: n0.river, status: n0.status, diffBankM: n0.diffBankM, distKm: n0.distKm, datetime: n0.datetime } : null,
      maxOverBankM: over?.diffBankM ?? null, overName: over?.name ?? null, nearBank: !!near, nearName: near?.name ?? null, outside,
    }) : errDim);
    // v0.9.10 A: that station's measured 6-h trend for the summary river row
    const tst = n0 ? { sid: n0.sid ?? null, name: n0.name } : outside ? { sid: outside.sid ?? null, name: outside.name } : null;
    if (!tst?.sid) publish(ctx.gen, "stationTrend", naDim);
    else void loadTwStation(tst.sid).then((d) => publish(ctx.gen, "stationTrend", d?.available && d.trend6
      ? okDim({ name: tst.name, kind: d.trend6.kind, cmPerHour: d.trend6.cmPerHour }) : errDim));
  }
  const headEl = document.getElementById("head-line");
  const setHead = (txt: string, cls: string): void => { if (headEl) { headEl.className = `head-line ${cls}`; headEl.textContent = txt; } };
  // Header priority (SPEC v0.9.1 §3): real gauges (POPNIX / ThaiWater) → satellite
  // → ประกาศ ปภ. → GloFAS. ThaiWater station line carries bank height + distance +
  // trend + time.
  const tw = nearestThaiWater(from.lat, from.lon, ctx.radiusKm);
  const twLine = tw ? `สถานี ${tw.name}${tw.river ? " (" + tw.river + ")" : ""}: ${tw.status === "over" && tw.diffBankM != null ? "สูงกว่าตลิ่ง " + tw.diffBankM.toFixed(2) + " ม." : "ใกล้ตลิ่ง"} · ห่าง ${tw.distKm.toFixed(1)} กม.${tw.trend === "up" ? " · แนวโน้ม ↑" : tw.trend === "down" ? " · แนวโน้ม ↓" : ""}${tw.datetime ? " · " + tw.datetime.slice(11) : ""}` : "";
  if (gaugeCount > 0) {
    const nearest = nearestFloodGauge(from.lat, from.lon, ctx.radiusKm);
    if (nearest && nearest.distKm > 10) setHead(`🌊 จุดวัดน้ำท่วมใกล้สุดอยู่ห่าง ${nearest.distKm.toFixed(1)} กม. (มี ${gaugeCount} จุดในรัศมี)`, "danger");
    else setHead(`🌊 พบจุดวัดน้ำท่วม ${gaugeCount} จุดในรัศมี ${ctx.radiusKm} กม.`, "danger");
  }
  else if (tw && tw.status === "over") setHead(`🌊 ${twLine} · ThaiWater`, "danger");
  else if (g.level === "flooded") setHead("🌊 อยู่ในพื้นที่ท่วม (ดาวเทียม)", "danger");
  else if (tw && tw.status === "near") setHead(`⚠️ ${twLine} · ThaiWater`, "warn");
  else if (prov?.declared) setHead("⚠️ อำเภอนี้มีประกาศน้ำท่วม", "warn");
  else if (g.level === "near") setHead("⚠️ ใกล้พื้นที่น้ำท่วม (ดาวเทียม)", "warn");
  else if (g.error) setHead("ตรวจพื้นที่น้ำท่วมไม่ได้ — โปรดดูประกาศพื้นที่", "warn");
  else setHead("💧 ยังไม่พบสัญญาณน้ำท่วมบริเวณนี้", "calm");

  if (g.level === "flooded" || g.level === "near") {
    const w = g.window ? WINDOW_TH[g.window] ?? "" : "";
    const near = g.level === "near" && g.nearestKm != null ? ` · พื้นที่ท่วม${awayWord()} ~${g.nearestKm.toFixed(1)} กม.` : "";
    nowChip.className = `chip ${g.level === "flooded" ? "veryhigh" : "high"}`;
    nowChip.textContent = `${g.level === "flooded" ? "🌊 น้ำท่วมพื้นที่นี้" : "⚠️ ใกล้พื้นที่น้ำท่วม"}${w ? " (" + w + ")" : ""}`;
    setNote(`${g.source ?? "GISTDA"}${g.observedAt ? " · ภาพ " + thDate(g.observedAt) : ""}${near}${isStale(g.observedAt) ? " · ข้อมูลเก่ากว่า 24 ชม." : ""}`, isStale(g.observedAt));
    onStatus(g.level === "flooded" ? "veryhigh" : "high");
    hideTrend();
    void loadDepth(from, ctx.gen);
  } else if (prov?.declared) {
    // ปภ. declared area — never green/ปกติ, even if GISTDA has not caught this spot
    nowChip.className = "chip high"; nowChip.textContent = "⚠️ อยู่ในพื้นที่ประกาศน้ำท่วม";
    setNote(`${declaredNote} · ดาวเทียมอาจตรวจในเขตชุมชนได้จำกัด — โปรดดูประกาศพื้นที่`);
    ddpmLink();
    onStatus("high");
    hideTrend();
  } else if (g.error) {
    nowChip.className = "chip unknown"; nowChip.textContent = "ตรวจพื้นที่ท่วมไม่ได้";
    setNote("ขณะนี้ตรวจสอบพื้นที่น้ำท่วม (GISTDA) ไม่ได้ — โปรดระวังและดูประกาศพื้นที่");
    onStatus("unknown");
  } else if (ctx.snapKm > ctx.radiusKm) {
    // A1: river used is outside the chosen radius — do not imply flow status here
    nowChip.className = "chip unknown"; nowChip.textContent = `ไม่มีลำน้ำหลักในรัศมี ${ctx.radiusKm} กม.`;
    setNote(`ลำน้ำที่ใกล้ที่สุดห่าง ~${ctx.snapKm.toFixed(1)} กม. · ดาวเทียมตรวจในเขตชุมชนได้จำกัด — โปรดดูประกาศพื้นที่`);
    onStatus("unknown");
  } else {
    // GISTDA none + not declared + river in radius → GloFAS, with community caveat
    if (reading) { nowChip.className = `chip ${reading.status}`; nowChip.textContent = reading.statusText; onStatus(reading.status); }
    else { nowChip.className = "chip unknown"; nowChip.textContent = "ไม่มีข้อมูล"; onStatus("unknown"); }
    setNote("ดาวเทียม GISTDA ไม่พบน้ำท่วมบริเวณนี้ (เขตชุมชนตรวจได้จำกัด) — โปรดดูประกาศพื้นที่");
  }
}

interface C13 { lat: number; lon: number; outflow_cms: number; basinAlertMinCms?: number; basinBbox?: { minLat: number; maxLat: number; minLon: number; maxLon: number } }

/** ⭐ C.13 (Chao Phraya Dam) release awareness:
 *  - On the C.13→gulf main stem → ETA card (when that water reaches the user).
 *  - Off the main stem but in the lower Chao Phraya basin AND in a flood/declared
 *    area AND release ≥ threshold → a no-ETA basin advisory (no false travel time). */
async function loadDamBanner(r: RouteResult, from: { lat: number; lon: number }, gen: number): Promise<void> {
  const host = document.getElementById("dam-banner");
  if (!host) return;
  try {
    const d = (await (await fetch("/data/dams-status.json")).json()) as { swocDate?: string; chaophraya?: C13 | null };
    if (gen !== renderGen) return;
    const c = d.chaophraya;
    if (!c || !c.outflow_cms) return;
    const cms = c.outflow_cms.toLocaleString("en-US");
    const credit = `ข้อมูล: ${SRC_FULL} · ${d.swocDate ?? ""}`;

    const r2 = routeTo(c.lon, c.lat, r.snapped.id);
    if (r2 && r2.reachedDest) {
      // on the main stem below C.13 → specific ETA (no basin card, avoid double)
      host.replaceChildren(el("div", { class: "dam-banner" },
        el("div", { class: "db-title" }, `🏞 เขื่อนเจ้าพระยาระบาย ${cms} ลบ.ม./วิ`),
        el("div", { class: "db-eta" }, `น้ำชุดนี้อาจถึง${spotWord()}เร็วสุด ~`, el("b", {}, fmtDays(r2.etaDaysFast)), " วัน",
          el("span", { class: "db-range" }, ` (ช่วง ${fmtDays(r2.etaDaysFast)}–${fmtDays(r2.etaDaysSlow)} วัน)`)),
        el("div", { class: "muted small" }, `${credit} · แบบจำลอง ยังไม่เทียบสถานีจริง`),
      ));
      return;
    }

    // basin-level advisory (no ETA): lower Chao Phraya basin + flood/declared + high release
    const minCms = c.basinAlertMinCms ?? 2000;
    const bb = c.basinBbox;
    const inBasin = !!bb && from.lat >= bb.minLat && from.lat <= bb.maxLat && from.lon >= bb.minLon && from.lon <= bb.maxLon;
    if (c.outflow_cms < minCms || !inBasin || !r.reachesGulf) return;
    // confirm the point is actually in a flood/declared area (don't alarm otherwise)
    let affected = false;
    try {
      const g = (await (await cachedFetch(`/api/gistda/flood?lat=${from.lat}&lon=${from.lon}`)).json()) as { level?: string; place?: { declared?: boolean } };
      if (gen !== renderGen) return;
      affected = g.level === "flooded" || g.level === "near" || g.place?.declared === true;
    } catch { affected = false; }
    if (!affected) return;
    host.replaceChildren(el("div", { class: "dam-banner basin" },
      el("div", { class: "db-title" }, `🏞 เขื่อนเจ้าพระยาระบาย ${cms} ลบ.ม./วิ ลงเจ้าพระยาตอนล่าง`),
      el("div", {}, "พื้นที่ลุ่มริมน้ำ/คลองเชื่อมอาจได้รับผลกระทบ — โปรดติดตามระดับน้ำ"),
      el("div", { class: "muted small" }, `${credit} · ไม่ใช่การพยากรณ์เวลาถึง${spotWord()}`),
    ));
  } catch { /* no banner on failure */ }
}

/** แม่น้ำตอนล่าง / น้ำทะเลหนุน (v0.9.4c): measured ThaiWater levels on the tidal reach +
 *  สทนช. announcements for the province. Nothing shown when neither applies. */
async function loadLowerRiverCard(from: { lat: number; lon: number }, gen: number, host: HTMLElement): Promise<void> {
  const [d, g] = await Promise.all([loadLowerRiver(from.lat, from.lon), fetchGistdaStatus(from)]);
  if (gen !== renderGen) return;
  publish(gen, "lowerRiver", d ? okDim(d) : errDim);
  if (!d || (!d.tidal && !d.alerts.length)) return;
  host.replaceChildren(lowerRiverCard(d, { hideFall: g.level === "flooded" || g.level === "near" || g.declared }));
}

interface DamRow { name: string; lat: number; lon: number; kind?: string; staleValue?: boolean; valueDate?: string; outflowCms: number | null; percent?: number | null; prev?: DamPrev | null }

/** v0.9.4a A3(ค)/A4 — for a point downstream of a dam: how its release changed
 *  since the previous SWOC report. The nearest upstream reservoir dam (along the
 *  river, same idea as Part B "ท้ายเขื่อน") + C.13 when the point is on its main stem.
 *  Amber = a significant rise (DAM_CHANGE); a significant fall = green; anything
 *  smaller / equal = a plain line (dam: only if it changed; C.13: always, + "ต่อเนื่อง
 *  N วัน" when the history bounds it). Any fall is hidden in a flooded/ปภ.-declared
 *  area (a "ลดลง" there could read as "safe"). No previous report → nothing (A5). */
async function loadDamChange(r: RouteResult, from: { lat: number; lon: number }, gen: number, host: HTMLElement): Promise<void> {
  try {
    const data = (await (await fetch("/data/dams-status.json")).json()) as { dams?: DamRow[]; chaophraya?: { lat: number; lon: number; outflow_cms: number; valueDate?: string | null; prev?: C13Prev | null; recent?: C13Recent[] | null } | null };
    if (gen !== renderGen) return;
    const lines: { text: string; hi: boolean; down: boolean; green: boolean; sub?: string; curDate: string; prevDate: string }[] = [];
    // nearest upstream reservoir dam whose snapped reach drains to the user's reach
    let best: { d: DamRow; km: number } | null = null;
    for (const d of data.dams ?? []) {
      if (d.kind === "diversion") continue;
      const rr = routeTo(d.lon, d.lat, r.snapped.id);
      if (!rr || !rr.reachedDest || rr.snapped.distKm > 2.5) continue;
      if (!best || rr.lengthKm < best.km) best = { d, km: rr.lengthKm };
    }
    const dc = best && !best.d.staleValue ? damReleaseChange(best.d) : null;
    if (best && dc && dc.dir !== "same") {
      lines.push({ text: releaseBarText(best.d.name, dc), hi: dc.highlight, down: dc.dir === "down", green: dc.arrow && dc.dir === "down", curDate: dc.curDate, prevDate: dc.prevDate });
    }
    const c = data.chaophraya;
    const c13OnPath = !!c && !!routeTo(c.lon, c.lat, r.snapped.id)?.reachedDest;
    // v0.9.5 สรุป: the upstream dam row (nearest reservoir; C.13 when the point is on its main stem)
    {
      const cc0 = c13OnPath ? c13Change(c!) : null;
      const amberText = dc?.highlight ? releaseBarText(best!.d.name, dc) : cc0?.highlight ? releaseBarText("เขื่อนเจ้าพระยา (C.13)", cc0).replace("(C.13)ระบาย", "(C.13) ระบาย") : null;
      const c13Info = c13OnPath && c ? { outflowCms: c.outflow_cms, change: cc0 } : null;
      publish(gen, "upstreamDam", best ? okDim({
        name: best.d.name, percent: best.d.staleValue ? null : best.d.percent ?? null, outflowCms: best.d.outflowCms, valueDate: best.d.valueDate ?? null,
        change: dc, releaseAmber: !!(dc?.highlight || cc0?.highlight), releaseText: amberText, c13: c13Info,
      }) : c13Info ? okDim({
        name: "เขื่อนเจ้าพระยา (C.13)", percent: null, outflowCms: c!.outflow_cms, valueDate: c!.valueDate ?? null,
        change: cc0, releaseAmber: !!cc0?.highlight, releaseText: amberText, c13: null,
      }) : naDim);
    }
    if (c) {
      const cc = c13OnPath ? c13Change(c) : null;
      if (cc) {
        const st = cc.dir === "same" ? c13Streak(c) : null;
        lines.push({ text: releaseBarText("เขื่อนเจ้าพระยา (C.13)", cc).replace("(C.13)ระบาย", "(C.13) ระบาย"), hi: cc.highlight, down: cc.dir === "down",
          green: cc.arrow && cc.dir === "down", ...(st ? { sub: `ระบายระดับนี้ต่อเนื่อง ${st.days} วัน (ตั้งแต่ ${thaiDay(st.since)})` } : {}), curDate: cc.curDate, prevDate: cc.prevDate });
      }
    }
    if (!lines.length) return;
    let shown = lines;
    if (lines.some((l) => l.down)) {
      const g = await fetchGistdaStatus(from);
      if (gen !== renderGen) return;
      if (g.level === "flooded" || g.level === "near" || g.declared) shown = lines.filter((l) => !l.down);
    }
    if (!shown.length) return;
    const first = shown[0]!;
    host.replaceChildren(el("div", { class: "dam-change" },
      ...shown.map((l) => el("div", { class: `dc-line${l.hi ? " hi" : ""}${l.green ? " down" : ""}` }, `${l.hi ? "🏞 " : ""}${l.text}`,
        ...(l.sub ? [el("div", { class: "muted small" }, l.sub)] : []))),
      el("div", { class: "muted small" }, `ที่มา: ${SRC_FULL} · รายงาน ${thaiDay(first.curDate)} เทียบ ${thaiDay(first.prevDate)}`),
    ));
  } catch { publish(gen, "upstreamDam", errDim); /* nothing shown on failure */ }
}

/** Nearest real POPNIX gauge over its bank within the radius (Bangkok only). */
async function loadGauge(from: { lat: number; lon: number }, radiusKm: number, gen: number): Promise<void> {
  const host = document.getElementById("gauge-host");
  if (!host) return;
  try {
    await loadGaugeData();
    if (gen !== renderGen) return;
    const g = nearestFloodGauge(from.lat, from.lon, radiusKm);
    if (!g) return; // no over-bank gauge nearby (or outside Bangkok)
    host.replaceChildren(el("div", { class: "gauge-card" + (g.fresh ? "" : " stale") },
      el("div", {}, `${depthIcon(g.overtopCm)} จุดวัดจริง (POPNIX): `, el("b", {}, g.name), g.fresh ? "" : el("span", { class: "stale-tag" }, " · เก่า")),
      el("div", { class: "muted small" }, `น้ำล้นตลิ่ง ~${Math.round(g.overtopCm)} ซม. (${depthLabel(g.overtopCm)}) · ห่าง ${g.distKm.toFixed(1)} กม.${g.measuredAt ? " · " + g.measuredAt : ""}${g.fresh ? "" : ` · ⚠️ เก่ากว่า ${2} ชม.`}`),
    ));
  } catch { /* no gauge line on failure */ }
}

function isStale(observedAt: string | null | undefined): boolean {
  if (!observedAt) return false;
  const t = Date.parse(observedAt + "T00:00:00+07:00");
  return Number.isFinite(t) && Date.now() - t > 24 * 3600 * 1000;
}

interface DepthResp { depthM: number | null; body: { key: string; icon: string; label: string; advice: string; severity: string } | null; note?: string; approximate?: boolean; }
const DEPTH_COLORS: Record<string, string> = { ankle: "#9fd3ff", knee: "#2f7bff", waist: "#7b3fe4", chest: "#5a189a", over: "#2b0a4d" };

async function loadDepth(from: { lat: number; lon: number }, gen: number): Promise<void> {
  const host = document.getElementById("depth-host");
  if (!host) return;
  host.replaceChildren(el("div", { class: "muted small" }, "กำลังประเมินความลึกโดยประมาณ…"));
  try {
    const r = await cachedFetch(`/api/depth?lat=${from.lat}&lon=${from.lon}`);
    if (gen !== renderGen) return;
    if (!r.ok) throw new Error(String(r.status));
    const d = (await r.json()) as DepthResp;
    if (d.depthM == null || !d.body) {
      host.replaceChildren(el("div", { class: "depth-na" }, "ความลึก: ประเมินไม่ได้ ณ จุดนี้ (อยู่ขอบพื้นที่ท่วม)"));
      return;
    }
    // Safety: coarse DEM under-estimates depth on flat floodplains. Never give a
    // reassuring low level there — show a caution instead of "เดินลุยได้".
    if (d.depthM < 0.3) {
      host.replaceChildren(el("div", { class: "depth-card sev-high" },
        el("div", { class: "depth-advice" }, "🌊 อยู่ในพื้นที่น้ำท่วม · ประเมินความลึกไม่ได้แม่นที่นี่ (พื้นที่ราบ/DEM หยาบ) — โปรดระวังและติดตามประกาศ")));
      return;
    }
    const dc = DEPTH_COLORS[d.body.key] ?? "#2f7bff";
    const card = el("div", { class: `depth-card sev-${d.body.severity}` },
      el("div", { class: "depth-top" },
        el("span", { class: "depth-icon" }, d.body.icon),
        el("div", {},
          el("div", { class: "depth-level" }, `${d.body.label} · ~${d.depthM!.toFixed(1)} ม.`, el("span", { class: "approx" }, "ประมาณ")),
          el("div", { class: "depth-advice", html: telize(d.body.advice) })),
      ),
    );
    card.style.borderColor = dc;
    card.style.background = `linear-gradient(90deg, ${dc}22, ${dc}0d)`;
    host.replaceChildren(card);
  } catch {
    host.replaceChildren(el("div", { class: "muted small" }, "ประเมินความลึกไม่ได้ขณะนี้"));
  }
}

function thDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const mo = ["ม.ค.","ก.พ.","มี.ค.","เม.ย.","พ.ค.","มิ.ย.","ก.ค.","ส.ค.","ก.ย.","ต.ค.","พ.ย.","ธ.ค."][(m ?? 1) - 1];
  return `${d} ${mo} ${((y ?? 2026) + 543) % 100}`;
}

function todayIndex(time: string[]): number {
  const today = new Date().toISOString().slice(0, 10);
  const i = time.indexOf(today);
  return i >= 0 ? i : Math.min(7, time.length - 1);
}

function readFlow(s: FloodSeries): FlowReading {
  const idx = todayIndex(s.time);
  const current = s.q[idx];
  if (current == null || !Number.isFinite(current) || current <= 0) {
    return { status: "unknown", statusText: "ไม่มีข้อมูล", trendText: "—", currentCms: null };
  }
  const sorted = [...s.q].filter((v) => v > 0).sort((a, b) => a - b);
  const median = sorted.length ? sorted[Math.floor(sorted.length / 2)]! : current;
  const ratio = current / (median || current);
  const status: FlowStatus = ratio >= 2 ? "veryhigh" : ratio >= 1.2 ? "high" : "normal";
  const statusText = status === "veryhigh" ? "สูงมาก" : status === "high" ? "สูง" : "ปกติ";
  const future = s.q[Math.min(idx + 3, s.q.length - 1)] ?? current;
  const chg = (future - current) / current;
  const trendText = chg > 0.15 ? "เพิ่มขึ้น ↑" : chg < -0.15 ? "ลดลง ↓" : "ทรงตัว →";
  return { status, statusText, trendText, currentCms: current };
}

function num(a: (number | null)[]): number[] { return a.map((v) => (typeof v === "number" ? v : 0)); }

function sparkline(s: FloodSeries, todayIdx: number): SVGElement {
  const W = 300, H = 56, pad = 4;
  const n = s.q.length;
  const all = [...s.q, ...s.min, ...s.max].filter((v) => v > 0);
  const lo = Math.min(...all), hi = Math.max(...all);
  const span = hi - lo || 1;
  const x = (i: number): number => pad + (i / Math.max(1, n - 1)) * (W - 2 * pad);
  const y = (v: number): number => H - pad - ((v - lo) / span) * (H - 2 * pad);
  const line = (arr: number[]): string => arr.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const band = s.max.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ") + " " +
    s.min.map((_, i) => `L${x(n - 1 - i).toFixed(1)},${y(s.min[n - 1 - i]!).toFixed(1)}`).join(" ") + " Z";
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", `0 0 ${W} ${H}`); svg.setAttribute("class", "spark");
  const p1 = document.createElementNS(ns, "path"); p1.setAttribute("d", band); p1.setAttribute("class", "spark-band");
  const p2 = document.createElementNS(ns, "path"); p2.setAttribute("d", line(s.q)); p2.setAttribute("class", "spark-line");
  const marker = document.createElementNS(ns, "line");
  marker.setAttribute("x1", String(x(todayIdx))); marker.setAttribute("x2", String(x(todayIdx)));
  marker.setAttribute("y1", String(pad)); marker.setAttribute("y2", String(H - pad)); marker.setAttribute("class", "spark-today");
  svg.append(p1, p2, marker);
  return svg;
}

/* ── v0.9.10 B: ground height of the chosen point (information only) ───────────────── */
interface ElevResp { elevationM: number | null; compare: "higher" | "lower" | "similar" | null; diffM: number | null; source: string; reason?: string }
async function loadElevation(from: { lat: number; lon: number }, gen: number): Promise<void> {
  try {
    const r = await cachedFetch(`/api/elevation?lat=${from.lat.toFixed(5)}&lon=${from.lon.toFixed(5)}`);
    if (!r.ok) throw new Error(String(r.status));
    const d = (await r.json()) as ElevResp;
    if (d.elevationM == null) { publish(gen, "elevation", naDim); if (gen === renderGen) setUserPinElev(null); return; }
    publish(gen, "elevation", okDim({ elevationM: d.elevationM, compare: d.compare, diffM: d.diffM, source: d.source }));
    if (gen === renderGen) setUserPinElev(elevationText(d.elevationM));
  } catch { publish(gen, "elevation", errDim); if (gen === renderGen) setUserPinElev(null); }
}
